/**
 * Rescigo · Bluetooth Thermal Printer utility (Iter 81)
 * --------------------------------------------------------------
 * Wraps the Web Bluetooth API + ESC/POS command builder so any
 * screen in the app can print a 58/80 mm receipt to a paired
 * printer with a single call:
 *
 *   import { thermalPrint } from "@/lib/thermalPrinter";
 *   await thermalPrint({ lines: [{text:"HELLO", size:2, align:"center"}] });
 *
 * Supported printers: any BLE thermal printer exposing the
 * standard "SPP-over-GATT" service (UUID 000018f0-...). This
 * covers the vast majority of 58 mm hand-held printers on the
 * market (PSF-580 / PSF-588, MTP-II, Zjiang, POS-58, GOOJPRT, etc.)
 *
 * Note on scope:
 *  - Web Bluetooth works on Chrome/Edge desktop + Chrome Android
 *    over HTTPS. iOS Safari does NOT support Web Bluetooth.
 *  - We negotiate MTU implicitly and chunk writes to 100 bytes
 *    with a 30 ms gap so slow BLE stacks don't drop packets.
 */

// -------- Bluetooth service / characteristic candidates --------
// Different printer firmwares advertise different UUIDs. We list
// the most common ones so the browser dialog picks up 95% of
// 58 mm printers without the user memorising anything.
const PRINTER_SERVICE_CANDIDATES = [
  "000018f0-0000-1000-8000-00805f9b34fb", // POS-58 / PSF-58 / MTP-II / Zjiang
  "0000ff00-0000-1000-8000-00805f9b34fb", // Some GOOJPRT
  "e7810a71-73ae-499d-8c15-faa9aef0c3f2", // Newer Xprinter
  "49535343-fe7d-4ae5-8fa9-9fafd205e455", // ISSC (BLE-UART, RN4677/BM77) – some Bixolon
];

const WRITE_CHAR_CANDIDATES = [
  "00002af1-0000-1000-8000-00805f9b34fb",
  "0000ff02-0000-1000-8000-00805f9b34fb",
  "bef8d6c9-9c21-4c9e-b632-bd58c1009f9f",
  "49535343-8841-43f4-a8d4-ecbe34729bb3",
];

// -------- Persistence keys (localStorage) --------
const LS = {
  paper: "rescigo.thermal.paper_width", // "58" | "80"
  last: "rescigo.thermal.last_device_name",
};

// -------- Small utilities --------
const enc = new TextEncoder();
const sleep = (ms) => new Promise(r => setTimeout(r, ms));

/** Read persisted paper width. Defaults to 58 mm. */
export function getPaperWidth() {
  const v = localStorage.getItem(LS.paper);
  return v === "80" ? 80 : 58;
}
export function setPaperWidth(mm) {
  localStorage.setItem(LS.paper, String(mm === 80 ? 80 : 58));
}
export function getCharsPerLine() {
  return getPaperWidth() === 80 ? 48 : 32;
}
export function getLastDeviceName() {
  return localStorage.getItem(LS.last) || "";
}

// -------- ESC/POS command bytes --------
// Reference: EPSON TM-T88 command set (widely cloned by 58 mm printers).
const ESC = 0x1b, GS = 0x1d;
const CMD = {
  init:        [ESC, 0x40],
  alignLeft:   [ESC, 0x61, 0x00],
  alignCenter: [ESC, 0x61, 0x01],
  alignRight:  [ESC, 0x61, 0x02],
  boldOn:      [ESC, 0x45, 0x01],
  boldOff:     [ESC, 0x45, 0x00],
  underlineOn: [ESC, 0x2d, 0x01],
  underlineOff:[ESC, 0x2d, 0x00],
  sizeNormal:  [GS,  0x21, 0x00],
  sizeDouble:  [GS,  0x21, 0x11],  // 2x width + 2x height
  sizeTall:    [GS,  0x21, 0x01],  // normal width, 2x height
  sizeWide:    [GS,  0x21, 0x10],  // 2x width, normal height
  cutPartial:  [GS,  0x56, 0x42, 0x03],
  feed:        (n)  => [ESC, 0x64, Math.max(1, Math.min(255, n | 0))],
};

/**
 * Convert a text line spec { text, align, size, bold, underline }
 * into a Uint8Array of ESC/POS bytes.
 */
function encodeLine(spec, width) {
  const bytes = [];
  const s = String(spec.text ?? "");
  const align = spec.align || "left";
  bytes.push(...(
    align === "center" ? CMD.alignCenter :
    align === "right"  ? CMD.alignRight  : CMD.alignLeft
  ));
  if (spec.bold)      bytes.push(...CMD.boldOn);
  if (spec.underline) bytes.push(...CMD.underlineOn);
  bytes.push(...(
    spec.size === 2 ? CMD.sizeDouble :
    spec.size === "tall" ? CMD.sizeTall :
    spec.size === "wide" ? CMD.sizeWide : CMD.sizeNormal
  ));
  // Text — soft-wrap by paper width so long addresses don't spill.
  const wrapWidth = spec.size === 2 || spec.size === "wide"
    ? Math.floor(width / 2)
    : width;
  const wrapped = wrapText(s, wrapWidth);
  for (const w of wrapped) {
    // ESC/POS default codepage 437; ASCII/₹→"Rs." (see receiptBuilder)
    bytes.push(...enc.encode(w));
    bytes.push(0x0a);
  }
  // Reset attributes for the next line
  bytes.push(...CMD.sizeNormal);
  if (spec.bold)      bytes.push(...CMD.boldOff);
  if (spec.underline) bytes.push(...CMD.underlineOff);
  return bytes;
}

/** Naïve word-wrap that respects hard newlines. */
function wrapText(s, w) {
  const out = [];
  for (const paragraph of s.split("\n")) {
    if (paragraph.length <= w) { out.push(paragraph); continue; }
    let line = "";
    for (const word of paragraph.split(/\s+/)) {
      if (!word) continue;
      if (line.length + word.length + (line ? 1 : 0) <= w) {
        line += (line ? " " : "") + word;
      } else if (word.length > w) {
        if (line) { out.push(line); line = ""; }
        // Break long tokens (URLs, invoice numbers) hard.
        for (let i = 0; i < word.length; i += w) {
          const chunk = word.slice(i, i + w);
          if (chunk.length === w) out.push(chunk);
          else line = chunk;
        }
      } else {
        out.push(line); line = word;
      }
    }
    if (line) out.push(line);
  }
  return out;
}

/**
 * Turn a receipt object → ESC/POS byte stream.
 * receipt: { lines: [{text, align?, size?, bold?, underline?}], cut?, feedAfter? }
 */
export function buildReceiptBytes(receipt) {
  const w = getCharsPerLine();
  const bytes = [];
  bytes.push(...CMD.init);
  for (const line of receipt.lines || []) {
    if (line.divider) {
      bytes.push(...(line.align === "center" ? CMD.alignCenter : CMD.alignLeft));
      bytes.push(...enc.encode("-".repeat(w)));
      bytes.push(0x0a);
      continue;
    }
    if (line.blank) { bytes.push(0x0a); continue; }
    if (line.row) {
      // Two-column row: [label, value] flushed to paper width.
      const [l, r] = line.row;
      const left = String(l || "");
      const right = String(r || "");
      const pad = Math.max(1, w - left.length - right.length);
      const rowStr = pad > 0
        ? left + " ".repeat(pad) + right
        : (left + " " + right).slice(0, w);
      bytes.push(...encodeLine({ text: rowStr, bold: line.bold, size: line.size }, w));
      continue;
    }
    bytes.push(...encodeLine(line, w));
  }
  bytes.push(...CMD.feed(receipt.feedAfter ?? 3));
  if (receipt.cut) bytes.push(...CMD.cutPartial);
  return new Uint8Array(bytes);
}

// -------- Bluetooth connection --------
let cachedChar = null;    // BluetoothRemoteGATTCharacteristic
let cachedDevice = null;  // BluetoothDevice

/** Detach current cached connection (used from Settings screen). */
export function disconnectPrinter() {
  try {
    if (cachedDevice?.gatt?.connected) cachedDevice.gatt.disconnect();
  } catch (_) { /* noop */ }
  cachedChar = null;
  cachedDevice = null;
}

/** Feature-detect Web Bluetooth. Returns human-readable reason or null. */
export function whyBluetoothUnavailable() {
  if (typeof navigator === "undefined" || !navigator.bluetooth) {
    // iOS Safari lands here; explain politely.
    if (/iP(hone|ad|od)/.test(navigator.userAgent)) {
      return "Bluetooth printing isn't supported inside iOS Safari. Please use an Android phone or a Chrome/Edge laptop.";
    }
    return "This browser doesn't support Web Bluetooth. Please use Chrome or Edge (desktop) or Chrome on Android.";
  }
  if (location.protocol !== "https:" && location.hostname !== "localhost") {
    return "Bluetooth printing needs a secure (https://) connection.";
  }
  return null;
}

async function pickWriteCharacteristic(server) {
  // Walk the service candidates and grab the first characteristic
  // that advertises WRITE. Some printers expose only one service,
  // some expose both — we don't care as long as one works.
  for (const svcUuid of PRINTER_SERVICE_CANDIDATES) {
    try {
      const svc = await server.getPrimaryService(svcUuid);
      const chars = await svc.getCharacteristics();
      for (const c of chars) {
        if (WRITE_CHAR_CANDIDATES.includes(c.uuid) ||
            c.properties.write || c.properties.writeWithoutResponse) {
          return c;
        }
      }
    } catch (_) { /* try next */ }
  }
  throw new Error("No writable characteristic on this printer. It may not be an ESC/POS BLE printer.");
}

/**
 * Prompt the browser's device picker, connect, and cache the
 * write characteristic. Call this from Settings → "Pair printer".
 * Returns { name, id }.
 */
export async function pairPrinter() {
  const why = whyBluetoothUnavailable();
  if (why) throw new Error(why);

  const device = await navigator.bluetooth.requestDevice({
    // acceptAllDevices avoids missing exotic PSF-588 UUIDs.
    // We still list every known service so the browser exposes them
    // to us after the user grants permission.
    acceptAllDevices: true,
    optionalServices: PRINTER_SERVICE_CANDIDATES,
  });
  if (!device) throw new Error("No printer selected.");
  device.addEventListener("gattserverdisconnected", () => {
    cachedChar = null;
    // Keep cachedDevice so we can retry the same one next click.
  });
  const server = await device.gatt.connect();
  const ch = await pickWriteCharacteristic(server);
  cachedDevice = device;
  cachedChar = ch;
  localStorage.setItem(LS.last, device.name || "Thermal printer");
  return { name: device.name || "Thermal printer", id: device.id };
}

/**
 * Try to reconnect silently to a previously-granted device. Chrome
 * 85+ exposes navigator.bluetooth.getDevices() which returns every
 * device the user ever granted, without popping a picker. Older
 * Chromes throw – we swallow that and fall back to pairPrinter.
 */
async function ensureConnected() {
  if (cachedChar && cachedDevice?.gatt?.connected) return cachedChar;
  if (cachedDevice) {
    try {
      const server = await cachedDevice.gatt.connect();
      cachedChar = await pickWriteCharacteristic(server);
      return cachedChar;
    } catch (_) { /* fall through */ }
  }
  if (navigator.bluetooth.getDevices) {
    try {
      const devs = await navigator.bluetooth.getDevices();
      const lastName = getLastDeviceName();
      const target = devs.find(d => d.name === lastName) || devs[0];
      if (target) {
        cachedDevice = target;
        target.addEventListener("gattserverdisconnected", () => { cachedChar = null; });
        const server = await target.gatt.connect();
        cachedChar = await pickWriteCharacteristic(server);
        return cachedChar;
      }
    } catch (_) { /* fall through */ }
  }
  // No silent path worked — the caller must ask the user to pair.
  throw new Error("NEEDS_PAIR");
}

/** Chunk-write a byte stream. Uses withoutResponse when supported. */
async function chunkedWrite(char, data) {
  const CHUNK = 100;                       // conservative BLE MTU
  const withoutResp = !!char.writeValueWithoutResponse
                     && char.properties.writeWithoutResponse;
  for (let i = 0; i < data.length; i += CHUNK) {
    const slice = data.slice(i, i + CHUNK);
    if (withoutResp) await char.writeValueWithoutResponse(slice);
    else             await char.writeValue(slice);
    // Give slow controllers a beat between packets.
    await sleep(30);
  }
}

/**
 * High-level print entry point.
 *
 *   await thermalPrint({ lines:[...], cut:true, feedAfter:4 })
 *
 * If no printer is paired yet, throws Error("NEEDS_PAIR") — caller
 * should surface a "Pair printer" prompt / toast.
 */
export async function thermalPrint(receipt) {
  const why = whyBluetoothUnavailable();
  if (why) throw new Error(why);
  const char = await ensureConnected();
  const bytes = buildReceiptBytes(receipt);
  await chunkedWrite(char, bytes);
  return { bytes: bytes.length };
}

/** Print a small "Rescigo · test print" ticket used from Settings. */
export async function testPrint() {
  return thermalPrint({
    lines: [
      { text: "RESCIGO HEALTH", align: "center", size: 2, bold: true },
      { text: "Bluetooth test print", align: "center" },
      { divider: true },
      { text: new Date().toString(), align: "center" },
      { blank: true },
      { row: ["Paper", `${getPaperWidth()} mm`] },
      { row: ["Chars/line", String(getCharsPerLine())] },
      { row: ["Device", getLastDeviceName() || "-"] },
      { divider: true },
      { text: "If you can read this line clearly, the printer is ready.", align: "center" },
      { blank: true },
    ],
    feedAfter: 4,
    cut: true,
  });
}
